import { doc, onSnapshot, serverTimestamp, setDoc, type Unsubscribe } from "firebase/firestore";
import type { User } from "firebase/auth";
import { db } from "./firebase";
import { defaultImageCompressionSettings, type ImageCompressionSettings } from "./imageCompression";

export type SupportAssistantSettings = {
  greeting: string;
  fallbackMessage: string;
  escalationPromptMessage: string;
};

export type SystemSettings = {
  imageCompression: ImageCompressionSettings;
  payment: PaymentSettings;
  supportAgentUserIds: string[];
  supportAssistant: SupportAssistantSettings;
};

export type PaymentSettings = {
  acceptCreditCard: boolean;
  acceptPix: boolean;
  acceptDebitCard: boolean;
  acceptBoleto: boolean;
  maxInstallments: number;
  defaultInstallments: number;
  statementDescriptor: string;
};

export const defaultPaymentSettings: PaymentSettings = {
  acceptCreditCard: true,
  acceptPix: true,
  acceptDebitCard: false,
  acceptBoleto: false,
  maxInstallments: 12,
  defaultInstallments: 1,
  statementDescriptor: "IMOBI"
};

export const defaultSupportAssistantSettings: SupportAssistantSettings = {
  greeting: "Oi! Sou o assistente automático do IMOBI. Descreva o que está acontecendo que eu tento ajudar antes de acionar o suporte.",
  fallbackMessage: "Não encontrei nada sobre isso na nossa base de conhecimento ainda, mas posso chamar o suporte técnico para te ajudar diretamente.",
  escalationPromptMessage: "Isso resolveu?"
};

export const defaultSystemSettings: SystemSettings = {
  imageCompression: defaultImageCompressionSettings,
  payment: defaultPaymentSettings,
  supportAgentUserIds: [],
  supportAssistant: defaultSupportAssistantSettings
};

function normalizeImageCompression(value: unknown): ImageCompressionSettings {
  const data = value && typeof value === "object" ? value as Partial<ImageCompressionSettings> : {};

  return {
    enabled: typeof data.enabled === "boolean" ? data.enabled : defaultImageCompressionSettings.enabled,
    maxWidth: typeof data.maxWidth === "number" ? data.maxWidth : defaultImageCompressionSettings.maxWidth,
    maxHeight: typeof data.maxHeight === "number" ? data.maxHeight : defaultImageCompressionSettings.maxHeight,
    quality: typeof data.quality === "number" ? data.quality : defaultImageCompressionSettings.quality
  };
}

function normalizeInstallments(value: unknown, fallback: number) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? Math.min(Math.max(Math.trunc(numberValue), 1), 24) : fallback;
}

function normalizeSupportAssistantSettings(value: unknown): SupportAssistantSettings {
  const data = value && typeof value === "object" ? value as Partial<SupportAssistantSettings> : {};

  return {
    greeting: typeof data.greeting === "string" && data.greeting.trim() ? data.greeting : defaultSupportAssistantSettings.greeting,
    fallbackMessage: typeof data.fallbackMessage === "string" && data.fallbackMessage.trim() ? data.fallbackMessage : defaultSupportAssistantSettings.fallbackMessage,
    escalationPromptMessage: typeof data.escalationPromptMessage === "string" && data.escalationPromptMessage.trim() ? data.escalationPromptMessage : defaultSupportAssistantSettings.escalationPromptMessage
  };
}

function normalizePaymentSettings(value: unknown): PaymentSettings {
  const data = value && typeof value === "object" ? value as Partial<PaymentSettings> : {};
  const maxInstallments = normalizeInstallments(data.maxInstallments, defaultPaymentSettings.maxInstallments);
  const defaultInstallments = Math.min(normalizeInstallments(data.defaultInstallments, defaultPaymentSettings.defaultInstallments), maxInstallments);

  return {
    acceptCreditCard: typeof data.acceptCreditCard === "boolean" ? data.acceptCreditCard : defaultPaymentSettings.acceptCreditCard,
    acceptPix: typeof data.acceptPix === "boolean" ? data.acceptPix : defaultPaymentSettings.acceptPix,
    acceptDebitCard: typeof data.acceptDebitCard === "boolean" ? data.acceptDebitCard : defaultPaymentSettings.acceptDebitCard,
    acceptBoleto: typeof data.acceptBoleto === "boolean" ? data.acceptBoleto : defaultPaymentSettings.acceptBoleto,
    maxInstallments,
    defaultInstallments,
    statementDescriptor: typeof data.statementDescriptor === "string" && data.statementDescriptor.trim()
      ? data.statementDescriptor.trim().slice(0, 22).toUpperCase()
      : defaultPaymentSettings.statementDescriptor
  };
}

export function subscribeSystemSettings(onChange: (settings: SystemSettings) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange(defaultSystemSettings);
    return () => undefined;
  }

  return onSnapshot(
    doc(db, "systemSettings", "app"),
    (snapshot) => {
      const data = snapshot.data();
      onChange({
        imageCompression: normalizeImageCompression(data?.imageCompression),
        payment: normalizePaymentSettings(data?.payment),
        supportAgentUserIds: Array.isArray(data?.supportAgentUserIds) ? data.supportAgentUserIds.map(String) : [],
        supportAssistant: normalizeSupportAssistantSettings(data?.supportAssistant)
      });
    },
    (error) => onError(error.message)
  );
}

export async function savePaymentSettings(settings: PaymentSettings, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await setDoc(doc(db, "systemSettings", "app"), {
    payment: normalizePaymentSettings(settings),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  }, { merge: true });
}

export async function saveImageCompressionSettings(settings: ImageCompressionSettings, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await setDoc(doc(db, "systemSettings", "app"), {
    imageCompression: settings,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  }, { merge: true });
}

export async function saveSupportAgents(userIds: string[], user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await setDoc(doc(db, "systemSettings", "app"), {
    supportAgentUserIds: userIds,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  }, { merge: true });
}

export async function saveSupportAssistantSettings(settings: SupportAssistantSettings, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await setDoc(doc(db, "systemSettings", "app"), {
    supportAssistant: normalizeSupportAssistantSettings(settings),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  }, { merge: true });
}
